import type { ReactNode, ReactElement } from 'react'
import { cn } from '@util'
import { isEmpty, type Milestone } from '@terros/common'
import { Link } from '@tanstack/react-router'
import { Text, Title, Group, Stack, Breadcrumbs, Anchor } from '@mantine/core'
import { Size, Typography } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { MilestonePill } from '../MilestonePill'
import { ButtonRefresh, ButtonBack, type ButtonBackProps } from '../buttons'

type TitleProps = {
  title: string | ReactElement
}

export type BreadCrumbItem = {
  label: string
  href: string
  disabled?: boolean
}

type PageBreadcrumbsProps = {
  breadcrumbs?: BreadCrumbItem[]
}

type PageTitleProps = TitleProps & {
  leftSection?: ReactNode
  milestone?: Milestone
  back?: boolean
} & ButtonBackProps

export type PageHeadingProps = PageTitleProps &
  PageBreadcrumbsProps & {
    refetch?: VoidFunction
    isFetching?: boolean
    children?: ReactNode
    middleSection?: ReactNode
    className?: string
    metadata?: {
      title?: string
    }
    rightSection?: ReactNode
  }

export const PageHeading = ({
  children,
  middleSection,
  refetch,
  isFetching,
  breadcrumbs,
  className,
  metadata: meta,
  rightSection,
  ...titleProps
}: PageHeadingProps): ReactNode => {
  const metaTitle = meta?.title || titleProps.title
  const hasActions = Boolean(children || refetch || rightSection)

  return (
    <div className={cn('border-default-border flex flex-col border-b backdrop-blur-sm', className)}>
      <Stack gap={Size.space.lg} px={Size.space['5xl']} py={Size.space['3xl']}>
        {typeof metaTitle === 'string' && <title>{`${metaTitle} - Terros`}</title>}
        <PageBreadcrumbs breadcrumbs={breadcrumbs} />
        <Group align='center' gap={Size.space['3xl']} justify='space-between' wrap='wrap'>
          <PageTitle {...titleProps} />
          {middleSection}
          <Group gap='sm'>
            {children}
            {refetch && <ButtonRefresh isFetching={isFetching} refetch={refetch} />}
            {rightSection}
          </Group>
        </Group>
      </Stack>
    </div>
  )
}

const PageTitle = ({ back, title, leftSection, milestone, ...backProps }: PageTitleProps): ReactElement => {
  return (
    <Group className='min-w-0' gap={Size.space['2xl']} wrap='nowrap'>
      {back && <ButtonBack {...backProps} />}
      {leftSection}
      <TitleComponent title={title} />
      {milestone && <MilestonePill milestone={milestone} />}
    </Group>
  )
}

const TitleComponent = ({ title }: TitleProps): ReactElement => {
  if (typeof title === 'string') {
    return <Title order={2}>{title}</Title>
  }
  return title
}

const PageBreadcrumbs = ({ breadcrumbs }: PageBreadcrumbsProps): ReactNode => {
  if (isEmpty(breadcrumbs)) return
  return (
    <Breadcrumbs
      separator={<MaterialSymbolIcon icon='chevron_right' size={Typography.size.md} />}
      separatorMargin={Size.space.xl}
      styles={{
        separator: {
          fontSize: Typography.size.lg,
        },
      }}
    >
      {breadcrumbs.map(({ label, href, disabled }, index) => {
        if (disabled) {
          return (
            <Text c='dimmed' key={index} size='md'>
              {label}
            </Text>
          )
        }
        return (
          <Anchor component={Link} key={index} size='sm' to={href}>
            {label}
          </Anchor>
        )
      })}
    </Breadcrumbs>
  )
}
